import { inject, Injectable } from '@angular/core';
import { indicate, prepare, updateRequestStatusOfKey } from '@ion/operators';
import { type StoreDef } from '@ngneat/elf';
import { selectIsRequestPending, updateRequestStatus } from '@ngneat/elf-requests';
import { TuiPopoverService } from '@taiga-ui/cdk';
import { TUI_DIALOGS } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { EMPTY, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { catchServerError } from '../catch-server-error/catch-server-error.operator';
import { NotificationHelper } from '../notification-helper.service';
import { ConfirmationModalComponent } from './confirmation-modal.component';
import {
  BusyControllerConfirmationOptions,
  type ConfirmationModalOptions,
  type ConfirmationNotification,
  StoreConfirmationOptions,
} from './confirmation-modal-options.model';

/**
 * A service for managing confirmation modals with advanced functionality.
 * Provides methods for displaying modals and integrating them with Observables
 * for user-confirmed actions. It also supports notification and request management.
 *
 * @extends TuiPopoverService
 *
 * @remarks
 * The inherited `open` method from `TuiPopoverService` is discouraged.
 * Use `applyBasicConfirmation`, `applyConfirmationWithNotification`,
 * `applyConfirmationForStore`, or `applyConfirmationForBusy` instead for better
 * integration with confirmation flow and helper functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfirmationService extends TuiPopoverService<ConfirmationModalOptions, boolean> {
  private readonly notify = inject(NotificationHelper);

  constructor() {
    super(TUI_DIALOGS, ConfirmationModalComponent, {
      title: '',
      heading: '',
      confirmButton: 'GENERAL.CONFIRM_CHANGES',
      cancelButton: 'GENERAL.CANCEL',
      params: {},
      doCompleteOnConfirm: true,
    } satisfies ConfirmationModalOptions);
  }

  /**
   * @deprecated Use `applyBasicConfirmation`, `applyConfirmationWithNotification`,
   * `applyConfirmationForStore`, or `applyConfirmationForBusy` instead.
   */
  override open: TuiPopoverService<ConfirmationModalOptions, boolean>['open'] = (
    ...args: Parameters<TuiPopoverService<ConfirmationModalOptions, boolean>['open']>
  ) => super.open(...args);

  /**
   * Opens a basic confirmation modal and executes provided an Observable when user confirms.
   *
   * @param options Partial configuration for the confirmation modal.
   * @param onConfirmationDo$ Observable to execute if the user confirms.
   * @returns Observable that emits the result of the confirmed action.
   */
  applyBasicConfirmation<T = unknown>(options: Partial<ConfirmationModalOptions>, onConfirmationDo$: Observable<T>) {
    return this.open(new PolymorpheusComponent(ConfirmationModalComponent), {
      title: '',
      heading: '',
      confirmButton: 'GENERAL.CONFIRM_CHANGES',
      cancelButton: 'GENERAL.CANCEL',
      params: {},
      doCompleteOnConfirm: true,
      ...options,
    }).pipe(switchMap(confirmed => (confirmed ? onConfirmationDo$ : EMPTY)));
  }

  /**
   * Opens a confirmation modal and executes provided an Observable when user confirms.
   * When stream emits a value it shows provided success notification (as translation key).
   *
   * @param options Partial configuration for the confirmation modal.
   * @param onConfirmationDo$ Observable to execute if the user confirms.
   * @param successNotification Notification message to display on success (translation key).
   * @returns Observable that emits the result of the confirmed action.
   */
  applyConfirmationWithNotification<T = unknown>(
    options: Partial<ConfirmationModalOptions>,
    onConfirmationDo$: Observable<T>,
    successNotification: string | ConfirmationNotification,
  ) {
    return this.applyBasicConfirmation<T>(options, onConfirmationDo$).pipe(
      tap({
        next: () => {
          if (successNotification) {
            if (typeof successNotification === 'string') {
              this.notify.success(successNotification);
            } else {
              const { messageKey, headlineKey, interpolateParams } = successNotification;
              this.notify.success(messageKey, headlineKey, interpolateParams);
            }
          }
        },
      }),
    );
  }

  /**
   * Opens a confirmation modal with advanced functionality on basis of a store (Elf):
   * - basic error handling
   * - loading and request management
   * - delays closing of modal until successful emission
   * - notification on success
   *
   * @param options Partial configuration for the confirmation modal.
   * @param onConfirmationDo$ Observable to execute if the user confirms.
   * @param advancedOptions Advanced options including store and notifications.
   * @returns Observable that emits the result of the confirmed action.
   */
  applyConfirmationForStore<T = unknown, S extends StoreDef = StoreDef>(
    options: Partial<ConfirmationModalOptions>,
    onConfirmationDo$: Observable<T>,
    advancedOptions: StoreConfirmationOptions<S>,
  ): Observable<T> {
    const { successNotification, store, key } = advancedOptions;
    // helper to control closing of confirmation modal
    const requestClose$ = new Subject<void>();
    const closeModal = () => {
      requestClose$.next();
      requestClose$.complete();
    };

    return this.open(new PolymorpheusComponent(ConfirmationModalComponent), {
      title: '',
      heading: '',
      confirmButton: 'GENERAL.CONFIRM_CHANGES',
      cancelButton: 'GENERAL.CANCEL',
      params: {},
      doCompleteOnConfirm: false,
      ...options,
      isLoading$: options.isLoading$ ?? store.pipe(selectIsRequestPending(key)),
    } as ConfirmationModalOptions).pipe(
      // eslint-disable-next-line rxjs/no-unsafe-takeuntil
      takeUntil(requestClose$), // Close modal gracefully
      prepare(() => store.update(updateRequestStatus(key, 'idle'))),
      switchMap(confirmed => {
        if (!confirmed) {
          closeModal();
          return EMPTY; // No action triggered
        }
        return onConfirmationDo$.pipe(
          prepare(() => store.update(updateRequestStatus(key, 'pending'))),
          updateRequestStatusOfKey(key, store),
          tap({
            next: () => {
              if (successNotification) {
                if (typeof successNotification === 'string') {
                  this.notify.success(successNotification);
                } else {
                  const { messageKey, headlineKey, interpolateParams } = successNotification;
                  this.notify.success(messageKey, headlineKey, interpolateParams);
                }
              }
              closeModal();
            },
          }),
          catchServerError(this.notify, advancedOptions.businessCodesReplacements),
        );
      }),
    );
  }

  /**
   * Opens a confirmation modal with advanced functionality on basis of a {@link BusyController}:
   * - basic error handling
   * - loading management
   * - delays closing of modal until successful emission
   * - notification on success
   *
   * @param options Partial configuration for the confirmation modal.
   * @param onConfirmationDo$ Observable to execute if the user confirms.
   * @param advancedOptions Advanced options including controller and notifications.
   * @returns Observable that emits the result of the confirmed action.
   */
  applyConfirmationForBusy<T = unknown>(
    options: Partial<ConfirmationModalOptions>,
    onConfirmationDo$: Observable<T>,
    advancedOptions: BusyControllerConfirmationOptions,
  ): Observable<T> {
    const { successNotification, busyController: controller } = advancedOptions;
    // helper to control closing of confirmation modal
    const requestClose$ = new Subject<void>();
    const closeModal = () => {
      requestClose$.next();
      requestClose$.complete();
    };

    return this.open(new PolymorpheusComponent(ConfirmationModalComponent), {
      title: '',
      heading: '',
      confirmButton: 'GENERAL.CONFIRM_CHANGES',
      cancelButton: 'GENERAL.CANCEL',
      params: {},
      doCompleteOnConfirm: false,
      ...options,
      isLoading$: options.isLoading$ ?? controller.isBusy$,
    } as ConfirmationModalOptions).pipe(
      // eslint-disable-next-line rxjs/no-unsafe-takeuntil
      takeUntil(requestClose$), // Close modal gracefully
      prepare(() => controller.idle()),
      switchMap(confirmed => {
        if (!confirmed) {
          closeModal();
          return EMPTY; // No action triggered
        }
        return onConfirmationDo$.pipe(
          indicate(controller.asSubject),
          tap({
            next: () => {
              if (successNotification) {
                if (typeof successNotification === 'string') {
                  this.notify.success(successNotification);
                } else {
                  const { messageKey, headlineKey, interpolateParams } = successNotification;
                  this.notify.success(messageKey, headlineKey, interpolateParams);
                }
              }
              closeModal();
            },
          }),
          catchServerError(this.notify),
        );
      }),
    );
  }
}
